import { type FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import { GENERIC_MAPS_ERROR } from '../../../constants/google-maps/message';
import { useGoogleMaps, useGoogleMapsGeocoder } from '../../../hooks/google/google-maps.hooks';
import {
	type GoogleLatLng,
	type GoogleMap,
	type GoogleMapBounds,
	type GoogleMarker,
	type LocationMarker
} from '../../../types/google-maps.types';
import { googleMap } from './map.css';

/**
 * Remove the given google map markers and their event listeners from the map.
 */
const removeMarkersFromMap = (googleApi: typeof google, mapMarkers: google.maps.marker.AdvancedMarkerElement[]): void => {
	mapMarkers?.forEach((mapMarker) => {
		googleApi.maps.event.clearInstanceListeners(mapMarker);
		mapMarker.remove();
	});
};

/**
 * Create and add the given location markers to the map as new map markers.
 * Returns the map markers that were added.
 */
const addMarkersToMap = (
	googleApi: typeof google,
	map: GoogleMap,
	locationMarkers: LocationMarker[]
): google.maps.marker.AdvancedMarkerElement[] => {
	return locationMarkers.map((locationMarker) => {
		const location = new googleApi.maps.LatLng(locationMarker.latitude, locationMarker.longitude);
		const pin = document.createElement('img');
		pin.src = '/public/assets/map_pin.png';
		const mapMarker = new googleApi.maps.marker.AdvancedMarkerElement({
			position: location,
			map,
			title: 'map marker pin',
			content: pin
		});

		const infoWindow = locationMarker.info
			? new googleApi.maps.InfoWindow({
					content: locationMarker.info
			  })
			: undefined;

		mapMarker.addListener('click', () => {
			infoWindow?.open(map, mapMarker);
			map.panTo(location);
			map.setZoom(10);
		});

		return mapMarker;
	});
};

export type MapProps = {
	// mapTypeControl: Decide whether to display the map type controller (satellite, terrain, etc) to the user
	// https://developers.google.com/maps/documentation/javascript/controls#Adding_Controls_to_the_Map
	mapTypeControl?: boolean;
	markers?: LocationMarker[];
	center?: LocationMarker;
	searchAddress?: string;
};

export const Map: FunctionComponent<MapProps> = ({ mapTypeControl, center, searchAddress, markers }) => {
	const ref = useRef<HTMLDivElement>(null);
	const [map, setMap] = useState<GoogleMap>();
	const [mapMarkers, setMapMarkers] = useState<google.maps.marker.AdvancedMarkerElement[]>([]);

	const { error: googleMapApiError, googleApi } = useGoogleMaps();
	const { marker: addressCenter } = useGoogleMapsGeocoder(center ? undefined : searchAddress);

	const initMap = useCallback(
		(zoomLevel: number, mapCenter?: GoogleLatLng) => {
			if (ref.current && googleApi) {
				setMap(
					new googleApi.maps.Map(ref.current, {
						zoom: zoomLevel,
						center: mapCenter,
						mapTypeControl,
						streetViewControl: false,
						zoomControl: true,
						mapTypeId: 'roadmap',
						mapId: 'roadmapId'
					})
				);
			}
		},
		[googleApi, mapTypeControl]
	);

	const defaultMapStart = useCallback(
		(centerLocation?: GoogleLatLng) => {
			initMap(8, centerLocation);
		},
		[initMap]
	);

	const extendAndFitBounds = (currentMap: GoogleMap, bounds: GoogleMapBounds, currentMarkers: GoogleMarker[]) => {
		currentMarkers.forEach((marker) => {
			const currentPosition = marker.position;
			if (currentPosition) {
				bounds.extend(currentPosition);
			}
		});
		currentMap.fitBounds(bounds);
	};

	useEffect((): void => {
		if (googleApi && !map) {
			const addressCenterOrGivenCenter = addressCenter ?? center;
			const centerLatLng = addressCenterOrGivenCenter
				? new googleApi.maps.LatLng(addressCenterOrGivenCenter.latitude, addressCenterOrGivenCenter.longitude)
				: undefined;
			defaultMapStart(centerLatLng);
		}
	}, [googleApi, map, center, defaultMapStart, searchAddress, addressCenter]);

	useEffect(() => {
		if (googleApi && map && markers) {
			// Remove any existing markers from the map before adding the new ones.
			removeMarkersFromMap(googleApi, mapMarkers);

			// Create and add new markers.
			setMapMarkers(addMarkersToMap(googleApi, map, markers));

			const bounds = new googleApi.maps.LatLngBounds();
			extendAndFitBounds(map, bounds, mapMarkers);
		}
		// The mapMarkers state is modified here; omitted to prevent infinite looping.
	}, [googleApi, map, markers]);

	useEffect(() => {
		const centerOrAddressCenter = searchAddress && addressCenter ? addressCenter : center;
		if (googleApi && map && centerOrAddressCenter) {
			// Center the map to the current center position.
			const centerLatLng = new googleApi.maps.LatLng(centerOrAddressCenter.latitude, centerOrAddressCenter.longitude);
			map.setCenter(centerLatLng);

			if (mapMarkers.length > 0) {
				const bounds = new googleApi.maps.LatLngBounds();
				extendAndFitBounds(map, bounds, mapMarkers);
			}
		}
	}, [googleApi, map, center, searchAddress, addressCenter]);

	return (
		<div data-testid="google-map-id" ref={ref} className={`${googleMap} chromatic-ignore vh-75 vh-50-m w-100`}>
			{googleMapApiError && GENERIC_MAPS_ERROR}
		</div>
	);
};
